import React from 'react';
import { Stack, Box, Card, FormLabel, Typography, Button } from '@mui/material';
import { useForm } from 'react-typed-form';
import {
  FieldMediaUpload,
  FieldLargeMediaUpload,
  FieldText,
  FieldTextRequired,
  FieldRadioGroup,
  FieldTags,
  FieldSwitch,
} from '../../form';
import { Model } from '../../models';
import { TiptapEditor } from '../../components/editor';
import PublishingSection from '../../components/PublishingSection';
import { UnsavedChangesBlocker } from '../../components';
import { usePublishing } from '../../hooks';
import { Api, useApiCallable } from '../../api';
import { ArrowClockwise, Download } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { iriToId, serverImageUrl } from '../../util';

type Props = {
  existing: Api.LibraryContent.V_DetailAsAdmin | null;
};

export default function LibraryForm({ existing }: Props) {
  const [regenerating, setRegenerating] = React.useState(false);

  const callApi = useApiCallable();

  const publishing = usePublishing(
    {
      contentType: 'Content',
      apiPath: 'library-contents',
      existingId: existing?.id,
    },
    () => form.handleSubmit(),
  );

  const initialValues = {
    title: existing?.title ?? '',
    type: existing?.type ?? 'article',
    body: existing?.body ?? null,
    references: existing?.references ?? null,
    question: existing?.question ?? null,
    transcriptEnabled: existing?.transcriptEnabled ?? false,
    speakerName: existing?.speakerName ?? null,
    manualTranscript: existing?.manualTranscript?.['@id'] ?? null,
    thumb: existing?.thumb?.['@id'] ?? null,
    media: existing?.media?.['@id'] ?? null,
    internalTags: existing?.internalTags.map((t) => t['@id']) ?? [],
    addQuestion: existing?.question !== null,
  };

  const form = useForm<{
    title: string;
    type: string;
    body: string | null;
    references: string | null;
    question: string | null;
    transcriptEnabled: boolean;
    speakerName: string | null;
    manualTranscript: string | null;
    thumb: string | null;
    media: string | null;
    internalTags: string[];
    addQuestion: boolean;
  }>({
    defaultValues: initialValues,
    postValidateTransform: (values) => {
      return {
        ...values,
        question: values.addQuestion ? values.question : null,
      };
    },
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      let response;

      if (!existing) {
        setLoading(true);
        response = await callApi(
          `/library-contents/${values.type}`,
          {
            jsonBody: values,
            method: 'POST',
          },
          {
            addSubmitError,
          },
        );
      } else {
        response = await callApi(
          `/library-contents/${existing.id}`,
          {
            method: 'PUT',
            jsonBody: values,
          },
          {
            addSubmitError,
          },
        );
      }

      setLoading(false);

      if (!response) return;

      await publishing.handleSubmitSuccess(response, !!existing);
    },
  });

  const tagsApi = Api.Tag.useReadCollectionAll();

  const contentType = form.getField('type').value;

  return (
    <Stack gap={3}>
      <FieldTextRequired
        field={form.getField('title')}
        label="Title"
        required
      />
      <FieldTags
        field={form.getField('internalTags')}
        label="Content Tags"
        options={
          tagsApi.data?.member.map((t) => ({
            label: `${t.category.name} / ${t.internalName}`,
            value: t['@id'],
          })) ?? []
        }
        required
      />
      <FieldRadioGroup
        field={form.getField('type')}
        label="Type"
        options={Model.LibraryContent.TYPE_OPTIONS.filter((o) => o.value)}
        required
      />
      <FieldMediaUpload
        field={form.getField('thumb')}
        label="Thumbnail"
        acceptedTypes={['IMAGE_TYPE']}
        helperText="4:3 aspect ratio"
        aspectRatio="4/3"
        existingAsset={existing?.thumb}
        required
      />
      {contentType === 'audio' && (
        <>
          <FieldMediaUpload
            field={form.getField('media')}
            label="Audio"
            acceptedTypes={['AUDIO_TYPE']}
            existingAsset={existing?.media}
            required
          />
          <FieldText
            field={form.getField('body')}
            label="Short Description"
            multiline
            rows={2}
          />
        </>
      )}
      {contentType === 'video' && (
        <>
          <FieldLargeMediaUpload
            field={form.getField('media')}
            label="Video File"
            acceptedTypes={['VIDEO_TYPE']}
            existingAsset={existing?.media}
            helperText="9:16 aspect ratio"
            required
          />
          <Box sx={{ display: 'flex' }}>
            <FieldSwitch
              field={form.getField('transcriptEnabled')}
              label="Add subtitles and gradient preset to the video?"
            />
          </Box>
          {form.getField('transcriptEnabled').value && (
            <>
              <Stack gap={2}>
                <Stack gap={1}>
                  <FormLabel>Transcript</FormLabel>

                  {existing?.autoTranscript ? (
                    <>
                      {existing?.autoTranscriptGeneratedAt && (
                        <Typography variant="body2" color="text.secondary">
                          Auto transcript generated{' '}
                          {format(
                            existing.autoTranscriptGeneratedAt,
                            'dd MMM HH:mm',
                          )}
                        </Typography>
                      )}
                      <Stack direction="row" gap={1}>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          href={serverImageUrl({
                            id: iriToId(existing.autoTranscript['@id']),
                          })}
                          target="_blank"
                        >
                          Download
                        </Button>
                        <Button
                          variant="text"
                          startIcon={<ArrowClockwise />}
                          onClick={() => {
                            setRegenerating(true);
                            callApi(
                              `/library-contents/${existing?.id}/regenerate-transcript`,
                              {
                                method: 'POST',
                                jsonBody: {},
                              },
                            );
                          }}
                          disabled={regenerating}
                        >
                          {regenerating ? 'Regenerating...' : 'Regenerate'}
                        </Button>
                      </Stack>
                    </>
                  ) : existing ? (
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Your transcript is generating
                      </Typography>
                      <Button
                        variant="text"
                        startIcon={<ArrowClockwise />}
                        onClick={() => {
                          setRegenerating(true);
                          callApi(
                            `/library-contents/${existing?.id}/regenerate-transcript`,
                            {
                              method: 'POST',
                              jsonBody: {},
                            },
                          );
                        }}
                        disabled={regenerating}
                      >
                        {regenerating ? 'Regenerating...' : 'Regenerate'}
                      </Button>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      A transcript will generate when the content is saved
                    </Typography>
                  )}
                </Stack>

                {/* <FieldMediaUpload
                  field={form.getField("manualTranscript")}
                  label="Transcript File"
                  acceptedTypes={["TRANSCRIPT_TYPE"]}
                  existingAsset={existing?.manualTranscript}
                  helperText="Uploading a manual transcript will override the automatic transcript"
                /> */}
              </Stack>
              <FieldText
                field={form.getField('speakerName')}
                label="Speaker Name"
                helperText="This name will show for a few seconds in the beginning of the video"
              />
            </>
          )}
        </>
      )}
      {contentType === 'article' && (
        <>
          <Stack gap={1}>
            <FormLabel required>Body</FormLabel>
            <Card sx={{ px: 2, pt: 0, pb: 2, maxWidth: 872 }}>
              <TiptapEditor
                content={form.getField('body').value ?? ''}
                onUpdate={(content) => {
                  form.getField('body').handleValueChange(content);
                }}
              />
            </Card>
          </Stack>
          <FieldText
            field={form.getField('references')}
            label="References"
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex' }}>
            <FieldSwitch
              field={form.getField('addQuestion')}
              label="Add a question at the end of this article?"
            />
          </Box>
          {form.getField('addQuestion').value && (
            <FieldText
              field={form.getField('question')}
              label="Question"
              helperText="max. 80 characters"
            />
          )}
        </>
      )}
      <PublishingSection
        existing={existing}
        isLoading={form.isLoading}
        contentType="Content"
        apiPath="library-contents"
        publishing={publishing}
      />

      <UnsavedChangesBlocker
        initialValues={initialValues}
        currentValues={form.values}
        isSubmittingRef={publishing.isSubmittingRef}
      />
    </Stack>
  );
}
